'use client';

import React, { Component } from 'react';
import { withAppRouter } from '@/legacy/withAppRouter';

import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import { CHECKOUT_STEP_SHIPPING_ADDRESS } from '@/constants/checkout';
import {
	ERR_5500,
	ERROR_PAYMENT_METHOD_MISSING,
} from '@/constants/errors';
import {
	TX_CART_SUMMARY_TITLE,
	TX_CHECKOUT_PLACE_ORDER,
	TX_CHECKOUT_PLACING_ORDER,
} from '@/constants/strings';
import { URL_SUCCESS } from '@/constants/urls';

import { Order } from '@/models/orders';

import { getSessionId } from '@/utils/auth';
import { 
	checkoutGetIsStoreCreditApplied, 
	checkoutGetSelectedPaymentMethod,
	checkoutGetSelectedShippingMethods,
	getCheckoutStepData, 
} from '@/utils/checkout';
import { formatServerError } from '@/utils/formatting';
import { getUrlParams } from '@/utils/request';

import CheckoutTotals from '@/components/Cart/totals/CheckoutTotals';
import LoadingIcon from '@/components/Icons/LoadingIcon';

import * as checkoutActionCreators from '@/actions/checkout';
let allActionCreators = Object.assign({}, checkoutActionCreators);

export class NoPaymentPayAtPickup extends Component {

	constructor(props) {
		super(props);

		this.state = {
			submitReqError: null,
			submitReqPending: false,
		};

		this.paymentMethod = checkoutGetSelectedPaymentMethod(this.props.checkout.stepData);
		this.shippingMethods = checkoutGetSelectedShippingMethods(this.props.checkout.stepData);
		this.shippingAddress = this.getShippingAddress();
		this.billingAddress = this.getBillingAddress();
	}

	handleSubmit(evt) {
		if(evt) { evt.preventDefault(); }
		this.completeSave();
	}

	getShippingAddress() {
		const stepData = getCheckoutStepData(CHECKOUT_STEP_SHIPPING_ADDRESS, this.props.checkout.stepData);
		if(stepData && stepData.data && stepData.data.shippingAddress) {
			return stepData.data.shippingAddress;
		}
		return null;
	}

	getBillingAddress() {
		return null;
	}

	getBuyOrderNumber() {
		const sessionId = getSessionId();
		const buyOrder = `tbk|${sessionId.substring(sessionId.length - 6)}|${Date.now()}`;

		return buyOrder;
	}

	loadingStart(cb) {
		document.body.style.overflow = 'hidden';
		this.setState({
			submitReqError: null,
			submitReqPending: true,
		}, () => {
			if(cb) { cb(); }
		});
	}

	loadingEnd(err, cb) {
		document.body.style.overflow = 'visible';
		this.setState({
			submitReqError: err || null,
			submitReqPending: false,
		}, () => {
			if(cb) { cb(); }
		});
	}

	async completeSave() {

		// Every exit from this callback must end loading: the "Placing order"
		// screen is a full-page overlay, so a path that returns without
		// loadingEnd() leaves the customer on an endless spinner.
		this.loadingStart(async () => {
			try {
				await this.submitOrder();
			} catch(err) {
				console.error(err);
				this.loadingEnd(ERR_5500);
			}
		});
	}

	async submitOrder() {

		if(!this.paymentMethod) {
			this.loadingEnd(ERROR_PAYMENT_METHOD_MISSING);
			return null;
		}

		// Read the shipping address from step data at submit time rather
		// than the snapshot taken in the constructor — covers the case
		// where the user enters the address after this component mounts.
		const submitShippingAddress = this.getShippingAddress();

		const shippingMethodUuids = this.shippingMethods
			.filter((locationMethod) => {
				return locationMethod.shippingMethod.publicUuid && locationMethod.location.publicUuid;
			})
			.map((locationMethod) => {
				return {
					shipping_method_uuid: locationMethod.shippingMethod.publicUuid,
					location_uuid: locationMethod.location.publicUuid,
				};
			});

		const orderData = {
			shipping_address_uuid: submitShippingAddress?.publicUuid ?? null,
			shipping_method_uuids: shippingMethodUuids,
			billing_address_uuid: this.billingAddress ? this.billingAddress.publicUuid : null,
			payment_method_uuid: this.paymentMethod.publicUuid,
			cart_uuid: this.props.cart.currentCart.publicUuid,
			payment_record_uuid: null,
			status: this.props.cart.currentCart.hasPreorderItems() ? Order.preorderStatus : Order.initialStatus,
			use_store_credit: checkoutGetIsStoreCreditApplied(this.props.checkout.stepData),
		};

		let orderReqFailed = false;
		const orderResp = await this.props.checkoutPlaceOrder(orderData)
			.catch((errResp) => {
				console.error(errResp);
				orderReqFailed = true;
				this.loadingEnd(formatServerError(errResp));
			});

		if(orderReqFailed) { return null; }
		if(!orderResp || !orderResp.public_uuid) {
			this.loadingEnd(ERR_5500);
			return null;
		}

		this.props.checkoutClearStepData();

		const uuidParams = {
			order: orderResp.public_uuid,
		}
		this.props.router.push(`${URL_SUCCESS}${getUrlParams(uuidParams)}`);
	}


	render() {

		const {t} = this.props;

		return <div className={'NoPaymentPayAtPickup CheckoutStepComponent'}>
			<div className='pmiLiner'>
				<div className={this.state.submitReqError ? 'noPaymentServerError present' : 'noPaymentServerError'}>
					{this.state.submitReqError ? t(this.state.submitReqError) : ''}
				</div>
				<form 
					className={'checkoutStepInputForm'} 
					onSubmit={this.handleSubmit.bind(this)}>
					<div className='checkoutSummaryWrapper'>
						<div className='summaryTitle'>{t(TX_CART_SUMMARY_TITLE)}</div>
						<CheckoutTotals
							cartLoading={this.state.cartLoading} />
					</div>
					<div className={'checkoutFormInput checkoutFormAction placeOrderAction'}>
						<button 
							type='submit'
							className='checkoutFormButton'
							disabled={this.state.submitReqPending}>
							{t(TX_CHECKOUT_PLACE_ORDER)}
						</button>
					</div>
				</form>
			</div>
			<div className={`checkoutSubmitScreen ${this.state.submitReqPending ? 'active' : ''}`}>
				<div className='submitScreenLiner'>
					<div className='submitIconWrapper'>
						<LoadingIcon />
					</div>
					<div className='submitIconLabel'>{t(TX_CHECKOUT_PLACING_ORDER)}</div>
				</div>
			</div>
		</div>;
	}
}

function mapStateToProps(state) {
	return {
		cart: state.cart, 
		checkout: state.checkout,
	};
}

export default connect(mapStateToProps, allActionCreators)(withTranslation()(withAppRouter(NoPaymentPayAtPickup)));